import { Box, type SelectChangeEvent } from '@mui/material';
import React from 'react';
import { InputLabelBase } from '../../../../../components/inputs/InputLabelBase';
import SelectBase from '../../../primeX-underwriter/components/footer/SelectBase';
import {
  GlobalFieldNames,
  type GlobalFieldValues,
  type PrimeXQuoteHandlers,
} from '../types';

import { deriveOptionFormatter } from '../utils';
import { useLocale } from '../providers/LocaleProvider';

const GlobalInput = ({ name, label, ...props }: GlobalInputProps) => {
  const locale = useLocale();
  const handleGlobalChange = (e: SelectChangeEvent<any>) => {
    if (!name) {
      return;
    }

    e.stopPropagation();
    if (name === GlobalFieldNames.retroactive) {
      return props.setGlobalValue?.({
        [name]: {
          props: {
            period: { value: e.target.value },
          },
        },
      });
    }

    props.setGlobalValue?.({
      [name]: {
        value: e.target.value,
      },
    });
  };

  const _options = props.options?.map(
    deriveOptionFormatter(locale.language, name)
  );

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={0.25}>
        <InputLabelBase
          style={{
            padding: 0,
            paddingBottom: '0.25rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            ...props.labelStyle,
          }}
        >
          {label}
        </InputLabelBase>
        {/* Accept and reject sit BESIDE the label here, not under the control.
            The globals header has one field per cell, so the label already
            names what the pair would resolve — and below the control is where
            the committed value goes. */}
        {props.labelAdornment}
      </Box>
      {props.children || (
        <SelectBase
          options={_options ?? []}
          name={name as string}
          value={props.value}
          onChange={handleGlobalChange}
          {...props.selectProps}
        />
      )}
    </Box>
  );
};

export default GlobalInput;

type GlobalInputProps = {
  label: string;
  children?: React.ReactNode;
  name?: string;
  options?: Array<string | number>;
  value?: string | number;
  setGlobalValue?: PrimeXQuoteHandlers['setGlobalValue'];
  selectProps?: Partial<React.ComponentProps<typeof SelectBase>>;
  /** Merged into the label's style, e.g. to size it like nearby headings. */
  readonly labelStyle?: React.CSSProperties;
  /** Rendered beside the label. The staged accept/reject pair, in practice. */
  readonly labelAdornment?: React.ReactNode;
} & GlobalFieldValues;
